import type {
	DeviceSharedVolume,
	DeviceSharedVolumeChangeKind,
	DeviceSharedVolumeEntry,
	DeviceSharedVolumePhase,
	DeviceSharedVolumes,
} from "@/lib/api";
import { sum } from "@/lib/sum";

/** 転送中に状態を取り直す間隔 (ms)。 */
export const DEVICE_SHARED_VOLUME_POLL_INTERVAL_MS = 3000;

/** 反映・取り込みを頼んだ後、ランナーが進み具合を報告し始めるまで状態を取り直す回数。 */
export const DEVICE_SHARED_VOLUME_ACTIVITY_POLLS = 3;

/** 最初の状態を取れなかったときに取り直す間隔 (ms)。 */
export const DEVICE_SHARED_VOLUME_RETRY_INTERVAL_MS = 10_000;

const TRANSFERRING_PHASES: ReadonlySet<DeviceSharedVolumePhase> = new Set([
	"pulling",
	"pushing",
	"pushPending",
]);

/** 手元で作ったもの (added) も書き換えたものと同じく「未反映」と出し、新規とは分けない。 */
export const DEVICE_SHARED_VOLUME_CHANGE_LABELS: Readonly<
	Record<DeviceSharedVolumeChangeKind, string>
> = {
	modified: "未反映",
	added: "未反映",
	deleted: "削除",
	unsupported: "対象外",
};

/** ボリュームの段階が転送中か (オフラインかは見ない)。 */
export function isDeviceSharedVolumeInTransfer(volume: DeviceSharedVolume): boolean {
	return TRANSFERRING_PHASES.has(volume.state.phase);
}

function hasTransferringVolume(volumes: readonly DeviceSharedVolume[]): boolean {
	return volumes.some(isDeviceSharedVolumeInTransfer);
}

/**
 * 転送 (取り込み・反映・反映待ち) が進んでいるか。オフラインのデバイスは進み具合を報告しないので、段階が転送中のまま
 * でも進んでいないとみなす。
 */
export function isDeviceSharedVolumeTransferring(overview: DeviceSharedVolumes): boolean {
	return overview.device.online && hasTransferringVolume(overview.volumes);
}

/** クラウドの一覧を取り直している (「更新」の後に裏で取り直している) ボリュームがあるか。 */
export function isDeviceSharedVolumeListingRefreshing(
	volumes: readonly DeviceSharedVolume[],
): boolean {
	return volumes.some((volume) => volume.listing?.refreshing === true);
}

/** 状態の取り直しで、転送が終わったか。 */
export function deviceSharedVolumeTransferFinished(
	previous: readonly DeviceSharedVolume[],
	next: readonly DeviceSharedVolume[],
): boolean {
	return hasTransferringVolume(previous) && !hasTransferringVolume(next);
}

/**
 * 状態の取り直しで、木の中身 (取り込み・反映の結果や手元の変更、クラウドの一覧) も変わったか。進み具合だけの変化では
 * 木を取り直さない。
 */
export function deviceSharedVolumeContentsChanged(
	previous: readonly DeviceSharedVolume[],
	next: readonly DeviceSharedVolume[],
): boolean {
	if (deviceSharedVolumeTransferFinished(previous, next)) return true;
	const before = new Map(previous.map((volume) => [volume.volumeId, volume]));
	return next.some((volume) => {
		const old = before.get(volume.volumeId);
		return (
			old === undefined ||
			old.state.lastPulledAt !== volume.state.lastPulledAt ||
			old.state.lastPushedAt !== volume.state.lastPushedAt ||
			old.state.scannedAt !== volume.state.scannedAt ||
			old.listing?.generation !== volume.listing?.generation ||
			(old.listing?.refreshing === true && volume.listing?.refreshing !== true)
		);
	});
}

/** 木の行のキー。ボリュームの根はボリューム ID だけ (ID は「/」を含まない)。 */
export function deviceSharedVolumeRowKey(volumeId: string, path: string): string {
	return path === "" ? volumeId : `${volumeId}/${path}`;
}

/** 親ディレクトリのパス (根の直下なら空文字)。 */
function parentSharedVolumePath(path: string): string {
	const slash = path.lastIndexOf("/");
	return slash === -1 ? "" : path.slice(0, slash);
}

function isSameOrBelow(path: string, ancestor: string): boolean {
	return ancestor === "" || path === ancestor || path.startsWith(`${ancestor}/`);
}

/** 写しを作るときに、写しの置き場に元からあったものを移したことの知らせ。移していなければ null。 */
export function describeMovedAside(volume: DeviceSharedVolume): string | null {
	const movedAsideTo = volume.state.movedAsideTo;
	if (movedAsideTo === null) return null;
	const parent = parentSharedVolumePath(volume.mountPath);
	return `前に ${volume.mountPath} にあったファイルを ${parent}/${movedAsideTo} に移しました (共有ボリュームには反映しません)`;
}

/** 行にアイコンで出す手元の変更の種類。手元で作ったもの (added) は書き換えたもの (modified) と同じに出す。 */
export type DeviceSharedVolumeEntryChange = Exclude<DeviceSharedVolumeChangeKind, "added">;

/** 行にアイコンで出す手元の変更の種類。無ければ null。 */
export function deviceSharedVolumeEntryChange(
	entry: DeviceSharedVolumeEntry,
): DeviceSharedVolumeEntryChange | null {
	if (entry.localChange === "added" || entry.localOnly) return "modified";
	if (entry.localChange !== null) return entry.localChange;
	if (entry.unsupported) return "unsupported";
	return null;
}

/** 行とその下の、反映していない手元の変更の数 (反映しても無くならない対象外は数えない)。 */
export function unpushedChangeCount(entry: DeviceSharedVolumeEntry): number {
	const own = entry.localChange === null || entry.localChange === "unsupported" ? 0 : 1;
	return own + entry.changedDescendants;
}

/** 行とその下の、手元の対象外 (symlink・FIFO など) の数。反映はしないが、写しを消せば消える。 */
export function localUnsupportedCount(entry: DeviceSharedVolumeEntry): number {
	return (entry.localChange === "unsupported" ? 1 : 0) + entry.unsupportedDescendants;
}

/**
 * 手元の変更を数えられなかったボリューム。根が symlink などで写しを確かめられないと、ランナーは失敗として理由を送り、
 * 件数は前に数えたときのまま残る。
 */
export function uncountedDeviceSharedVolumes(
	volumes: readonly DeviceSharedVolume[],
): DeviceSharedVolume[] {
	return volumes.filter(
		(volume) => volume.state.phase === "failed" && volume.state.lastError !== null,
	);
}

/** 数えられなかったボリュームを確認に出す文。 */
export function uncountedDeviceSharedVolumesMessage(
	volumes: readonly DeviceSharedVolume[],
): string {
	const list = volumes.map((volume) => `「${volume.name}」(${volume.state.lastError})`).join("、");
	return `共有ボリューム${list}は手元の変更を数えられませんでした。反映していない変更があるかもしれません。`;
}

/** 数え直した結果の、反映していない変更の合計と、数えられなかったボリュームの文。 */
export interface DeviceSharedVolumeScanSummary {
	unpushed: number;
	/** 数え直せた (false なら命令を送れず、最後に確かめたときの件数)。 */
	counted: boolean;
	/** 数えられなかったボリュームがあれば、その文。 */
	uncountedMessage: string | null;
}

export function summarizeScan(
	scanned: Pick<DeviceSharedVolumes, "device" | "volumes">,
): DeviceSharedVolumeScanSummary {
	const uncounted = uncountedDeviceSharedVolumes(scanned.volumes);
	return {
		unpushed: sum(scanned.volumes, (volume) => volume.state.unpushedChangeCount),
		counted: scanned.device.unavailableReason === null,
		uncountedMessage: uncounted.length > 0 ? uncountedDeviceSharedVolumesMessage(uncounted) : null,
	};
}

/** 取り込んでいないものとして灰色で出すか。手元にだけあるものは取り込んだものと同じに出す。 */
export function isDeviceSharedVolumeEntryMuted(entry: DeviceSharedVolumeEntry): boolean {
	return (entry.inclusion === "excluded" && !entry.localOnly) || entry.unsupported;
}

/** 右クリックの対象 1 件。`entry` が null ならボリュームの根。 */
export interface DeviceSharedVolumeTarget {
	volumeId: string;
	path: string;
	entry: DeviceSharedVolumeEntry | null;
}

/** ボリュームごとに、入れ子を除いた対象のパス。根を含むボリュームは `null` (全体)。 */
export function groupDeviceSharedVolumeTargets(
	targets: readonly DeviceSharedVolumeTarget[],
): Map<string, string[] | null> {
	const groups = new Map<string, string[] | null>();
	for (const target of targets) {
		if (target.path === "") groups.set(target.volumeId, null);
		else if (!groups.has(target.volumeId)) groups.set(target.volumeId, []);
	}
	for (const [volumeId, paths] of groups) {
		if (paths === null) continue;
		const own = targets
			.filter((target) => target.volumeId === volumeId)
			.map((target) => target.path)
			.sort((a, b) => a.length - b.length);
		for (const path of own) {
			if (!paths.some((kept) => isSameOrBelow(path, kept))) paths.push(path);
		}
	}
	return groups;
}

/** 写しから消す範囲の、手元にしか無いものの数え。 */
export interface UnpushedChangeTally {
	/** 反映していない変更。 */
	count: number;
	/** 手元の対象外 (反映はしないが、消せば消える)。 */
	unsupported: number;
	/**
	 * 一部しか数えられなかったので、実際はもっと多いかもしれない (変更の一覧が打ち切られたボリュームの一部を数えた・
	 * ボリュームの根の直下を読みきれていない)。
	 */
	partial: boolean;
	/** 手元の変更を数えられなかったボリューム。 */
	uncounted: DeviceSharedVolume[];
}

/**
 * 対象の下の、反映していない手元の変更と対象外の数 (入れ子は数え直さない)。根の変更はボリューム全体の数で、対象外は
 * 読み込んだ根の直下 (directories) から数える。
 */
export function countUnpushedChanges(
	targets: readonly DeviceSharedVolumeTarget[],
	volumes: readonly DeviceSharedVolume[],
	directories: ReadonlyMap<string, DeviceSharedVolumeDirectory>,
): UnpushedChangeTally {
	const tally: UnpushedChangeTally = { count: 0, unsupported: 0, partial: false, uncounted: [] };
	const uncounted = new Set(uncountedDeviceSharedVolumes(volumes));
	for (const [volumeId, paths] of groupDeviceSharedVolumeTargets(targets)) {
		const volume = volumes.find((candidate) => candidate.volumeId === volumeId);
		if (volume && uncounted.has(volume)) tally.uncounted.push(volume);
		if (paths === null) {
			tally.count += volume?.state.unpushedChangeCount ?? 0;
			const root = directories.get(deviceSharedVolumeRowKey(volumeId, ""));
			if (!root || root.nextCursor !== null) tally.partial = true;
			tally.unsupported += sum(root?.entries ?? [], localUnsupportedCount);
			continue;
		}
		if (volume?.state.changesTruncated) tally.partial = true;
		for (const target of targets) {
			if (target.volumeId === volumeId && target.entry && paths.includes(target.path)) {
				tally.count += unpushedChangeCount(target.entry);
				tally.unsupported += localUnsupportedCount(target.entry);
			}
		}
	}
	return tally;
}

export type DeviceSharedVolumeActivityTone = "warning" | "danger" | "muted";

/** 共有ボリュームの根の行の上に出す同期の知らせ (失敗・注意)。 */
interface DeviceSharedVolumeActivity {
	/** 行を見分ける。 */
	id: "transfer" | "refresh-error" | "restart";
	message: string;
	tone: DeviceSharedVolumeActivityTone;
	/** 補足 (失敗の詳細)。 */
	detail: string | null;
}

/**
 * 転送の知らせを 1 つにまとめる。複数のボリュームがあるときは、気づくべきものを優先する。取り込み・反映の進み具合は
 * 文言にせず根の行のスピナーで示すので、知らせが無ければ null。
 */
function describeTransfer(
	overview: DeviceSharedVolumes,
): Omit<DeviceSharedVolumeActivity, "id"> | null {
	const { device, volumes } = overview;
	const inPhase = (phase: DeviceSharedVolumePhase) =>
		volumes.filter((volume) => volume.state.phase === phase);
	const named = (volume: DeviceSharedVolume, message: string) =>
		volumes.length > 1 ? `${volume.name}: ${message}` : message;

	if (device.revoked && device.unavailableReason !== null) {
		return { message: device.unavailableReason, tone: "warning", detail: null };
	}
	if (!device.online) {
		const waiting = inPhase("pushing").length > 0 || inPhase("pushPending").length > 0;
		return {
			message: waiting ? "反映待ち (デバイスがオフライン)" : "デバイスがオフラインです",
			tone: "muted",
			detail: null,
		};
	}
	if (!device.runnerSupported && device.unavailableReason !== null) {
		return { message: device.unavailableReason, tone: "warning", detail: null };
	}
	const [failed] = inPhase("failed");
	if (failed) {
		return {
			message: named(failed, "取り込み・反映に失敗しました"),
			tone: "danger",
			detail: failed.state.lastError,
		};
	}
	const [noSpace] = inPhase("stoppedNoSpace");
	if (noSpace) {
		return {
			message: named(noSpace, "空き容量が足りないため取り込みを止めました"),
			tone: "warning",
			detail: noSpace.state.lastError,
		};
	}
	return null;
}

/** 一部のボリュームだけの様子なら、1 つのときはどれかを名前で示す。 */
function scopedMessage(
	matched: readonly { name: string }[],
	overview: DeviceSharedVolumes,
	message: string,
): string {
	return matched.length === 1 && overview.volumes.length > 1
		? `${matched[0].name}: ${message}`
		: message;
}

/** 根の行の上に出す行 (転送の知らせと、クラウドの一覧の取り直しの失敗、再起動が要ること)。 */
export function describeDeviceSharedVolumeActivity(
	overview: DeviceSharedVolumes,
): DeviceSharedVolumeActivity[] {
	const lines: DeviceSharedVolumeActivity[] = [];
	const transfer = describeTransfer(overview);
	if (transfer) lines.push({ id: "transfer", ...transfer });
	const relistFailures = overview.volumes.flatMap((volume) =>
		volume.listing?.refreshError && !volume.listing.refreshing
			? [{ name: volume.name, error: volume.listing.refreshError }]
			: [],
	);
	if (relistFailures.length > 0) {
		lines.push({
			id: "refresh-error",
			message: scopedMessage(relistFailures, overview, "一覧を取り直せませんでした"),
			tone: "danger",
			detail:
				relistFailures.length === 1
					? relistFailures[0].error
					: relistFailures.map((failure) => `${failure.name}: ${failure.error}`).join("\n"),
		});
	}
	if (overview.device.online && overview.volumes.some((volume) => volume.state.restartRequired)) {
		lines.push({
			id: "restart",
			message: "エージェントを再起動すると、共有ボリュームが見えるようになります",
			tone: "warning",
			detail: null,
		});
	}
	return lines;
}

/** 読み込んだディレクトリの直下。 */
export interface DeviceSharedVolumeDirectory {
	entries: DeviceSharedVolumeEntry[];
	/** 続きの頁の cursor。無ければ最後まで読んだ。 */
	nextCursor: string | null;
}

/** 木の 1 行。`entry` が null ならボリュームの根。 */
export interface DeviceSharedVolumeEntryRow {
	type: "entry";
	key: string;
	volumeId: string;
	path: string;
	name: string;
	depth: number;
	directory: boolean;
	/** 開ける (ディレクトリか、使える状態のボリュームの根)。 */
	expandable: boolean;
	expanded: boolean;
	entry: DeviceSharedVolumeEntry | null;
	volume: DeviceSharedVolume;
}

/** ディレクトリの直下の続きを読む行。 */
interface DeviceSharedVolumeLoadMoreRow {
	type: "loadMore";
	key: string;
	volumeId: string;
	path: string;
	depth: number;
}

type DeviceSharedVolumeRow = DeviceSharedVolumeEntryRow | DeviceSharedVolumeLoadMoreRow;

/** ボリュームを最上位のフォルダにし、展開して読み込んだディレクトリの中身を描画順に並べる。 */
export function buildDeviceSharedVolumeRows(
	volumes: readonly DeviceSharedVolume[],
	directories: ReadonlyMap<string, DeviceSharedVolumeDirectory>,
	expanded: ReadonlySet<string>,
): DeviceSharedVolumeRow[] {
	const rows: DeviceSharedVolumeRow[] = [];
	const appendChildren = (volume: DeviceSharedVolume, path: string, depth: number) => {
		const dirKey = deviceSharedVolumeRowKey(volume.volumeId, path);
		const directory = directories.get(dirKey);
		if (!directory) return;
		for (const entry of directory.entries) {
			const key = deviceSharedVolumeRowKey(volume.volumeId, entry.path);
			const isDirectory = entry.kind === "directory";
			const isExpanded = isDirectory && expanded.has(key);
			rows.push({
				type: "entry",
				key,
				volumeId: volume.volumeId,
				path: entry.path,
				name: entry.name,
				depth,
				directory: isDirectory,
				expandable: isDirectory,
				expanded: isExpanded,
				entry,
				volume,
			});
			if (isExpanded) appendChildren(volume, entry.path, depth + 1);
		}
		if (directory.nextCursor !== null) {
			rows.push({
				type: "loadMore",
				key: `${dirKey}\n続き`,
				volumeId: volume.volumeId,
				path,
				depth,
			});
		}
	};
	for (const volume of volumes) {
		const key = deviceSharedVolumeRowKey(volume.volumeId, "");
		const expandable = volume.volumeState === "ready";
		const isExpanded = expandable && expanded.has(key);
		rows.push({
			type: "entry",
			key,
			volumeId: volume.volumeId,
			path: "",
			name: volume.name,
			depth: 0,
			directory: true,
			expandable,
			expanded: isExpanded,
			entry: null,
			volume,
		});
		if (isExpanded) appendChildren(volume, "", 1);
	}
	return rows;
}

/** 読み込んだディレクトリから 1 件を探す。根や見つからないものは null。 */
export function findDeviceSharedVolumeEntry(
	directories: ReadonlyMap<string, DeviceSharedVolumeDirectory>,
	volumeId: string,
	path: string,
): DeviceSharedVolumeEntry | null {
	if (path === "") return null;
	const parent = directories.get(deviceSharedVolumeRowKey(volumeId, parentSharedVolumePath(path)));
	return parent?.entries.find((entry) => entry.path === path) ?? null;
}

/** 手元の写しにあるか (パスをコピー・メッセージに追加できる)。根は null で、いつもある。 */
export function isDeviceSharedVolumeEntryLocal(entry: DeviceSharedVolumeEntry | null): boolean {
	if (entry === null) return true;
	if (entry.locallyDeleted) return false;
	if (entry.localOnly || entry.localChange !== null) return true;
	return entry.inclusion !== "excluded" && !entry.unsupported;
}

/** 手元の写しのファイルとして開ける (読み取り専用) か。 */
export function isDeviceSharedVolumeEntryOpenable(entry: DeviceSharedVolumeEntry | null): boolean {
	return (
		entry !== null &&
		entry.kind === "file" &&
		entry.localChange !== "unsupported" &&
		isDeviceSharedVolumeEntryLocal(entry)
	);
}

/** 写しの中の絶対パス (根は mountPath)。 */
export function deviceSharedVolumeLocalPath(volume: DeviceSharedVolume, path: string): string {
	return path === "" ? volume.mountPath : `${volume.mountPath}/${path}`;
}

/**
 * 行に落としたものを置く写しのディレクトリ (根からのパス。ファイルの行は親)。取り込んでいない行にも置ける (置いたものは
 * 手元で作ったものになり、反映すると取り込む規則に足される)。使える状態のボリュームで、対象外と手元で消した行は null。
 */
export function deviceSharedVolumeDropDirectory(row: DeviceSharedVolumeEntryRow): string | null {
	if (row.volume.volumeState !== "ready" || row.volume.state.restartRequired) return null;
	if (row.entry?.unsupported === true || row.entry?.locallyDeleted === true) return null;
	return row.directory ? row.path : parentSharedVolumePath(row.path);
}
